/**
 * Block editor logic (Notion-like), pure and translated to Swift/Kotlin.
 *
 * The page body is a Loro doc (contracts/docs.ts): a MovableTree of blocks, each with `type`, `props` and a rich
 * LoroText. The platform's `EditorDoc` port exposes it as a plain `EditorSnapshot`; every intent here takes a
 * snapshot and returns `EditOp`s for the port to apply (a tree move, a text insert, a mark, …) plus where the caret
 * goes next. `applyOps` is the reference semantics of those ops (the Loro adapters must match it; vectors check it).
 *
 * Inline atoms (mentions) are one U+FFFC character carrying the `mention` mark. Offsets are UTF-16 code units.
 */
import type { BlockProps, BlockType } from "@bundle/contracts";

// ---------- Model ----------

export type MentionKind = "user" | "page" | "date";
export type MentionMark = { kind: MentionKind; id?: string; date?: string };

/** A run of text with the same marks (one entry of the LoroText delta). */
export type TextSpan = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  code?: boolean;
  underline?: boolean;
  link?: string;
  mention?: MentionMark;
};

export type EditorBlock = {
  id: string;
  type: BlockType;
  props: BlockProps;
  /** Plain text; inline atoms are U+FFFC. Empty for blocks without text (divider, image, …). */
  text: string;
  /** Formatting of `text` (concatenated span texts == text); empty when there is no text. */
  spans: TextSpan[];
  /** Absent ⇒ top level. */
  parentId?: string;
  /** Child block ids in order. */
  children: string[];
};

export type EditorSnapshot = { blocks: Record<string, EditorBlock>; roots: string[] };

export type Caret = { blockId: string; offset: number };

/** Boolean inline marks toggled with ⌘B / ⌘I / ⌘⇧S / ⌘E / ⌘U. */
export type FormatMark = "bold" | "italic" | "strike" | "code" | "underline";

export type EditOp =
  /** New block at `index` among the children of `parentId` (absent ⇒ top level). */
  | { op: "insertBlock"; id: string; parentId?: string; index: number; type: BlockType; props: BlockProps; text: string }
  /** Deletes the block and its subtree. */
  | { op: "deleteBlock"; id: string }
  /** Moves the block (with its subtree) to `index` among the children of `parentId`, counted after removing it. */
  | { op: "moveBlock"; id: string; parentId?: string; index: number }
  | { op: "setType"; id: string; type: BlockType }
  /** Replaces the block's props. */
  | { op: "setProps"; id: string; props: BlockProps }
  | { op: "insertText"; id: string; index: number; text: string }
  | { op: "deleteText"; id: string; index: number; length: number }
  /** Moves text [start, end) of `from` (with its marks) to offset `at` of `to` (a different block). */
  | { op: "moveText"; from: string; start: number; end: number; to: string; at: number }
  | { op: "format"; id: string; start: number; end: number; mark: FormatMark; on: boolean }
  /** Sets (url present) or removes (absent) a link on [start, end). */
  | { op: "link"; id: string; start: number; end: number; url?: string }
  /** Inserts a mention atom (U+FFFC with the mention mark). */
  | { op: "insertMention"; id: string; index: number; mention: MentionMark };

/**
 * What an intent did: ops to apply, where the caret goes, or blocks to select (a void block such as an image is
 * selected instead of merged). `handled: false` ⇒ let the platform's text input do its default.
 */
export type EditResult = { ops: EditOp[]; focus?: Caret; select?: string[]; handled: boolean };

const NOT_HANDLED: EditResult = { ops: [], handled: false };
const HANDLED: EditResult = { ops: [], handled: true };

export const ATOM = "￼";

// ---------- Block kinds ----------

const TEXT_TYPES: BlockType[] = ["paragraph", "heading1", "heading2", "heading3", "bulleted", "numbered", "todo", "toggle", "quote", "callout", "code"];
const PARENT_TYPES: BlockType[] = ["paragraph", "bulleted", "numbered", "todo", "toggle", "quote", "callout"];
const LIST_TYPES: BlockType[] = ["bulleted", "numbered", "todo", "toggle"];

/** Has an editable rich-text field. */
export function hasText(type: BlockType): boolean {
  return TEXT_TYPES.includes(type);
}

/** Can contain nested blocks (indent target). */
export function canHaveChildren(type: BlockType): boolean {
  return PARENT_TYPES.includes(type);
}

/** List-like: Enter continues the same type, Enter on an empty one ends the list. */
export function isListType(type: BlockType): boolean {
  return LIST_TYPES.includes(type);
}

/** Props a block gets when it becomes `type` (a to-do starts unchecked; everything else starts plain). */
export function defaultProps(type: BlockType): BlockProps {
  if (type === "todo") return { checked: false };
  if (type === "code") return { language: "plain" };
  return {};
}

// ---------- Tree helpers ----------

function block(doc: EditorSnapshot, id: string): EditorBlock | undefined {
  return doc.blocks[id];
}

/** Child ids of `parentId` (absent ⇒ top level). */
export function childrenOf(doc: EditorSnapshot, parentId: string | undefined): string[] {
  if (parentId === undefined) return doc.roots;
  return doc.blocks[parentId]?.children ?? [];
}

/** Index of the block among its siblings (-1 when missing). */
export function indexOf(doc: EditorSnapshot, id: string): number {
  const b = block(doc, id);
  if (b === undefined) return -1;
  return childrenOf(doc, b.parentId).indexOf(id);
}

/** The block itself and every descendant, depth first. */
export function subtree(doc: EditorSnapshot, id: string): string[] {
  const out: string[] = [];
  const walk = (x: string) => {
    out.push(x);
    for (const c of doc.blocks[x]?.children ?? []) walk(c);
  };
  if (doc.blocks[id] !== undefined) walk(id);
  return out;
}

/** Is `ancestorId` a strict ancestor of `id`? */
export function isAncestor(doc: EditorSnapshot, ancestorId: string, id: string): boolean {
  let p = block(doc, id)?.parentId;
  while (p !== undefined) {
    if (p === ancestorId) return true;
    p = block(doc, p)?.parentId;
  }
  return false;
}

/** A row of the rendered page: blocks in reading order, minus the children of collapsed toggles. */
export type VisibleRow = { id: string; depth: number; /** 1-based position in a run of numbered siblings. */ number?: number };

export function visibleRows(doc: EditorSnapshot, collapsed: string[]): VisibleRow[] {
  const out: VisibleRow[] = [];
  const walk = (ids: string[], depth: number) => {
    let n = 0;
    for (const id of ids) {
      const b = doc.blocks[id];
      if (b === undefined) continue;
      n = b.type === "numbered" ? n + 1 : 0;
      out.push(b.type === "numbered" ? { id, depth, number: n } : { id, depth });
      if (!(b.type === "toggle" && collapsed.includes(id))) walk(b.children, depth + 1);
    }
  };
  walk(doc.roots, 0);
  return out;
}

/** Every block in reading order (collapsed toggles included). */
export function documentOrder(doc: EditorSnapshot): string[] {
  return visibleRows(doc, []).map((r) => r.id);
}

/** The visible block before `id` (any type), or undefined at the top. */
export function previousVisible(doc: EditorSnapshot, id: string, collapsed: string[]): string | undefined {
  const rows = visibleRows(doc, collapsed);
  const i = rows.findIndex((r) => r.id === id);
  return i > 0 ? rows[i - 1]!.id : undefined;
}

/** The visible block after `id` (any type), or undefined at the bottom. */
export function nextVisible(doc: EditorSnapshot, id: string, collapsed: string[]): string | undefined {
  const rows = visibleRows(doc, collapsed);
  const i = rows.findIndex((r) => r.id === id);
  return i >= 0 && i + 1 < rows.length ? rows[i + 1]!.id : undefined;
}

/** Caret target for ↑ on the first line: the end of the previous visible text block. */
export function caretUp(doc: EditorSnapshot, id: string, collapsed: string[]): Caret | undefined {
  const rows = visibleRows(doc, collapsed);
  for (let i = rows.findIndex((r) => r.id === id) - 1; i >= 0; i--) {
    const b = doc.blocks[rows[i]!.id]!;
    if (hasText(b.type)) return { blockId: b.id, offset: b.text.length };
  }
  return undefined;
}

/** Caret target for ↓ on the last line: the start of the next visible text block. */
export function caretDown(doc: EditorSnapshot, id: string, collapsed: string[]): Caret | undefined {
  const rows = visibleRows(doc, collapsed);
  const at = rows.findIndex((r) => r.id === id);
  if (at < 0) return undefined;
  for (let i = at + 1; i < rows.length; i++) {
    const b = doc.blocks[rows[i]!.id]!;
    if (hasText(b.type)) return { blockId: b.id, offset: 0 };
  }
  return undefined;
}

/** Visible blocks from `anchorId` to `focusId` inclusive, in reading order (multi-block selection, desktop). */
export function selectRange(doc: EditorSnapshot, anchorId: string, focusId: string, collapsed: string[]): string[] {
  const ids = visibleRows(doc, collapsed).map((r) => r.id);
  const a = ids.indexOf(anchorId);
  const f = ids.indexOf(focusId);
  if (a < 0 || f < 0) return [];
  return ids.slice(Math.min(a, f), Math.max(a, f) + 1);
}

/** The selected blocks whose ancestors are not selected (the subtrees an operation acts on), in reading order. */
export function selectionRoots(doc: EditorSnapshot, ids: string[]): string[] {
  const set = new Set(ids);
  return documentOrder(doc).filter((id) => set.has(id) && !ids.some((other) => other !== id && isAncestor(doc, other, id)));
}

// ---------- Spans ----------

type MarkKey = "bold" | "italic" | "strike" | "code" | "underline" | "link" | "mention";
const MARK_KEYS: MarkKey[] = ["bold", "italic", "strike", "code", "underline", "link", "mention"];
/** Marks that grow when typing at their end (Loro `expand: "after"`, ProseMirror `inclusive`). */
const EXPANDS_AFTER: MarkKey[] = ["bold", "italic", "strike", "underline"];

function markValue(s: TextSpan, k: MarkKey): unknown {
  return s[k];
}

function sameMark(a: unknown, b: unknown): boolean {
  if (a === undefined || b === undefined) return a === b;
  return JSON.stringify(a) === JSON.stringify(b);
}

function sameMarks(a: TextSpan, b: TextSpan): boolean {
  return MARK_KEYS.every((k) => sameMark(markValue(a, k), markValue(b, k)));
}

function withText(s: TextSpan, text: string): TextSpan {
  return { ...s, text };
}

/** Merges adjacent spans with the same marks and drops empty ones. */
export function normalizeSpans(spans: TextSpan[]): TextSpan[] {
  const out: TextSpan[] = [];
  for (const s of spans) {
    if (s.text === "") continue;
    const last = out[out.length - 1];
    // Mention atoms never merge: each is its own inline object.
    if (last !== undefined && last.mention === undefined && s.mention === undefined && sameMarks(last, s)) out[out.length - 1] = withText(last, last.text + s.text);
    else out.push(s);
  }
  return out;
}

/** The spans covering [start, end). */
export function sliceSpans(spans: TextSpan[], start: number, end: number): TextSpan[] {
  const out: TextSpan[] = [];
  let pos = 0;
  for (const s of spans) {
    const from = Math.max(start, pos);
    const to = Math.min(end, pos + s.text.length);
    if (from < to) out.push(withText(s, s.text.slice(from - pos, to - pos)));
    pos += s.text.length;
  }
  return out;
}

/** Marks of the character at `index` (undefined outside the text). */
function marksAt(spans: TextSpan[], index: number): TextSpan | undefined {
  let pos = 0;
  for (const s of spans) {
    if (index >= pos && index < pos + s.text.length) return s;
    pos += s.text.length;
  }
  return undefined;
}

/**
 * Marks new text typed at `index` gets: a mark both neighbours share, or one that expands after its end
 * (bold/italic/strike/underline) from the character before. Links, code and mentions never grow at their edges.
 */
export function inheritedMarks(spans: TextSpan[], index: number): TextSpan {
  const left = index > 0 ? marksAt(spans, index - 1) : undefined;
  const right = marksAt(spans, index);
  const out: TextSpan = { text: "" };
  for (const k of MARK_KEYS) {
    if (k === "mention") continue;
    const l = left === undefined ? undefined : markValue(left, k);
    const r = right === undefined ? undefined : markValue(right, k);
    if (l !== undefined && sameMark(l, r)) (out as Record<string, unknown>)[k] = l;
    else if (l !== undefined && EXPANDS_AFTER.includes(k)) (out as Record<string, unknown>)[k] = l;
  }
  return out;
}

function insertSpans(spans: TextSpan[], index: number, inserted: TextSpan[]): TextSpan[] {
  const total = spans.reduce((n, s) => n + s.text.length, 0);
  return normalizeSpans([...sliceSpans(spans, 0, index), ...inserted, ...sliceSpans(spans, index, total)]);
}

function deleteSpans(spans: TextSpan[], start: number, end: number): TextSpan[] {
  const total = spans.reduce((n, s) => n + s.text.length, 0);
  return normalizeSpans([...sliceSpans(spans, 0, start), ...sliceSpans(spans, end, total)]);
}

function mapSpans(spans: TextSpan[], start: number, end: number, f: (s: TextSpan) => TextSpan): TextSpan[] {
  const total = spans.reduce((n, s) => n + s.text.length, 0);
  return normalizeSpans([...sliceSpans(spans, 0, start), ...sliceSpans(spans, start, end).map(f), ...sliceSpans(spans, end, total)]);
}

/** Does every character of [start, end) carry `mark`? (false for an empty range) */
export function hasMark(b: EditorBlock, start: number, end: number, mark: FormatMark): boolean {
  if (end <= start) return false;
  return sliceSpans(b.spans, start, end).every((s) => s[mark] === true);
}

/** The link on [start, end) when the whole range carries the same one. */
export function linkAt(b: EditorBlock, start: number, end: number): string | undefined {
  const parts = sliceSpans(b.spans, start, Math.max(end, start + 1));
  const first = parts[0]?.link;
  return first !== undefined && parts.every((s) => s.link === first) ? first : undefined;
}

// ---------- Reference semantics of ops ----------

function put(blocks: Record<string, EditorBlock>, b: EditorBlock): void {
  blocks[b.id] = b;
}

function withChildren(doc: { blocks: Record<string, EditorBlock>; roots: string[] }, parentId: string | undefined, children: string[]): void {
  if (parentId === undefined) doc.roots = children;
  else {
    const p = doc.blocks[parentId];
    if (p !== undefined) put(doc.blocks, { ...p, children });
  }
}

function setParent(blocks: Record<string, EditorBlock>, id: string, parentId: string | undefined): void {
  const b = blocks[id]!;
  const next: EditorBlock = { ...b };
  if (parentId === undefined) delete next.parentId;
  else next.parentId = parentId;
  put(blocks, next);
}

function clampIndex(i: number, len: number): number {
  return Math.max(0, Math.min(len, i));
}

/** Applies ops in order (the reference for every EditorDoc implementation). Invalid ops are skipped. */
export function applyOps(doc: EditorSnapshot, ops: EditOp[]): EditorSnapshot {
  const d = { blocks: { ...doc.blocks }, roots: doc.roots };
  for (const op of ops) {
    switch (op.op) {
      case "insertBlock": {
        if (d.blocks[op.id] !== undefined) break;
        if (op.parentId !== undefined && d.blocks[op.parentId] === undefined) break;
        const b: EditorBlock = { id: op.id, type: op.type, props: op.props, text: op.text, spans: op.text === "" ? [] : [{ text: op.text }], children: [] };
        if (op.parentId !== undefined) b.parentId = op.parentId;
        put(d.blocks, b);
        const sibs = [...childrenOf(d, op.parentId)];
        sibs.splice(clampIndex(op.index, sibs.length), 0, op.id);
        withChildren(d, op.parentId, sibs);
        break;
      }
      case "deleteBlock": {
        const b = d.blocks[op.id];
        if (b === undefined) break;
        for (const x of subtree(d, op.id)) delete d.blocks[x];
        withChildren(d, b.parentId, childrenOf(d, b.parentId).filter((x) => x !== op.id));
        break;
      }
      case "moveBlock": {
        const b = d.blocks[op.id];
        if (b === undefined) break;
        if (op.parentId !== undefined && (d.blocks[op.parentId] === undefined || op.parentId === op.id || isAncestor(d, op.id, op.parentId))) break;
        withChildren(d, b.parentId, childrenOf(d, b.parentId).filter((x) => x !== op.id));
        const sibs = [...childrenOf(d, op.parentId)];
        sibs.splice(clampIndex(op.index, sibs.length), 0, op.id);
        withChildren(d, op.parentId, sibs);
        setParent(d.blocks, op.id, op.parentId);
        break;
      }
      case "setType": {
        const b = d.blocks[op.id];
        if (b !== undefined) put(d.blocks, { ...b, type: op.type });
        break;
      }
      case "setProps": {
        const b = d.blocks[op.id];
        if (b !== undefined) put(d.blocks, { ...b, props: op.props });
        break;
      }
      case "insertText": {
        const b = d.blocks[op.id];
        if (b === undefined || op.text === "") break;
        const i = clampIndex(op.index, b.text.length);
        const marks = inheritedMarks(b.spans, i);
        put(d.blocks, { ...b, text: b.text.slice(0, i) + op.text + b.text.slice(i), spans: insertSpans(b.spans, i, [withText(marks, op.text)]) });
        break;
      }
      case "deleteText": {
        const b = d.blocks[op.id];
        if (b === undefined) break;
        const s = clampIndex(op.index, b.text.length);
        const e = clampIndex(op.index + op.length, b.text.length);
        if (e <= s) break;
        put(d.blocks, { ...b, text: b.text.slice(0, s) + b.text.slice(e), spans: deleteSpans(b.spans, s, e) });
        break;
      }
      case "moveText": {
        const from = d.blocks[op.from];
        const to = d.blocks[op.to];
        if (from === undefined || to === undefined || op.from === op.to) break;
        const s = clampIndex(op.start, from.text.length);
        const e = clampIndex(op.end, from.text.length);
        if (e <= s) break;
        const moved = sliceSpans(from.spans, s, e);
        put(d.blocks, { ...from, text: from.text.slice(0, s) + from.text.slice(e), spans: deleteSpans(from.spans, s, e) });
        const target = d.blocks[op.to]!;
        const at = clampIndex(op.at, target.text.length);
        put(d.blocks, { ...target, text: target.text.slice(0, at) + from.text.slice(s, e) + target.text.slice(at), spans: insertSpans(target.spans, at, moved) });
        break;
      }
      case "format": {
        const b = d.blocks[op.id];
        if (b === undefined) break;
        const s = clampIndex(op.start, b.text.length);
        const e = clampIndex(op.end, b.text.length);
        if (e <= s) break;
        put(d.blocks, {
          ...b,
          spans: mapSpans(b.spans, s, e, (x) => {
            const y = { ...x };
            if (op.on) y[op.mark] = true;
            else delete y[op.mark];
            return y;
          }),
        });
        break;
      }
      case "link": {
        const b = d.blocks[op.id];
        if (b === undefined) break;
        const s = clampIndex(op.start, b.text.length);
        const e = clampIndex(op.end, b.text.length);
        if (e <= s) break;
        put(d.blocks, {
          ...b,
          spans: mapSpans(b.spans, s, e, (x) => {
            const y = { ...x };
            if (op.url === undefined) delete y.link;
            else y.link = op.url;
            return y;
          }),
        });
        break;
      }
      case "insertMention": {
        const b = d.blocks[op.id];
        if (b === undefined) break;
        const i = clampIndex(op.index, b.text.length);
        put(d.blocks, { ...b, text: b.text.slice(0, i) + ATOM + b.text.slice(i), spans: insertSpans(b.spans, i, [{ text: ATOM, mention: op.mention }]) });
        break;
      }
    }
  }
  return { blocks: d.blocks, roots: d.roots };
}

// ---------- Structure intents ----------

/** Ops that move `id` out of its parent to right after it; its following siblings become its children (order kept). */
function outdentOps(doc: EditorSnapshot, id: string): EditOp[] {
  const b = block(doc, id);
  if (b === undefined || b.parentId === undefined) return [];
  const parent = block(doc, b.parentId)!;
  const sibs = parent.children;
  const following = sibs.slice(sibs.indexOf(id) + 1);
  const ops: EditOp[] = [];
  if (following.length > 0 && canHaveChildren(b.type)) {
    let n = b.children.length;
    for (const f of following) ops.push({ op: "moveBlock", id: f, parentId: id, index: n++ });
  }
  const grand = parent.parentId;
  const at = childrenOf(doc, grand).indexOf(parent.id) + 1;
  ops.push(grand === undefined ? { op: "moveBlock", id, index: at } : { op: "moveBlock", id, parentId: grand, index: at });
  return ops;
}

/** Tab: each block (selection roots, reading order) becomes the last child of its previous sibling. */
export function indent(doc: EditorSnapshot, ids: string[]): EditResult {
  let d = doc;
  const ops: EditOp[] = [];
  for (const id of selectionRoots(doc, ids)) {
    const b = block(d, id);
    if (b === undefined) continue;
    const sibs = childrenOf(d, b.parentId);
    const prevId = sibs[sibs.indexOf(id) - 1];
    const prev = prevId === undefined ? undefined : block(d, prevId);
    if (prev === undefined || !canHaveChildren(prev.type)) continue;
    const op: EditOp = { op: "moveBlock", id, parentId: prev.id, index: prev.children.length };
    ops.push(op);
    d = applyOps(d, [op]);
  }
  return { ops, handled: true };
}

/** Shift-Tab: each nested block moves out to right after its parent (last selected first, so order is kept). */
export function outdent(doc: EditorSnapshot, ids: string[]): EditResult {
  let d = doc;
  const ops: EditOp[] = [];
  for (const id of selectionRoots(doc, ids).toReversed()) {
    const next = outdentOps(d, id);
    ops.push(...next);
    d = applyOps(d, next);
  }
  return { ops, handled: true };
}

/** Changes the type of text blocks (turn into / markdown / slash), resetting type-specific props. */
export function turnInto(doc: EditorSnapshot, ids: string[], type: BlockType): EditResult {
  const ops: EditOp[] = [];
  for (const id of ids) {
    const b = block(doc, id);
    if (b === undefined || !hasText(b.type) || !hasText(type)) continue;
    if (b.type !== type) ops.push({ op: "setType", id, type });
    const props = keepColor(b.props, defaultProps(type));
    if (JSON.stringify(props) !== JSON.stringify(b.props)) ops.push({ op: "setProps", id, props });
  }
  return { ops, handled: true };
}

function keepColor(from: BlockProps, props: BlockProps): BlockProps {
  return from.color === undefined ? props : { ...props, color: from.color };
}

/** Type of the block Enter creates after `type` (lists continue; everything else starts a paragraph). */
function continuation(type: BlockType): BlockType {
  return type === "bulleted" || type === "numbered" || type === "todo" ? type : "paragraph";
}

/**
 * Enter at [start, end) in a text block.
 * - code: a newline inside the block;
 * - empty list item: nested ⇒ outdent, top level ⇒ becomes a paragraph;
 * - caret at the very start of a non-empty block: an empty block is inserted above, the caret stays;
 * - otherwise the text after the caret moves into a new block below (lists continue, others become paragraphs).
 *   A block with children (and an open toggle) gets the new block as its first child.
 */
export function enter(doc: EditorSnapshot, blockId: string, start: number, end: number, newId: string): EditResult {
  const b = block(doc, blockId);
  if (b === undefined || !hasText(b.type)) return NOT_HANDLED;
  const ops: EditOp[] = [];
  if (end > start) ops.push({ op: "deleteText", id: blockId, index: start, length: end - start });
  const text = b.text.slice(0, start) + b.text.slice(end);
  if (b.type === "code") {
    ops.push({ op: "insertText", id: blockId, index: start, text: "\n" });
    return { ops, focus: { blockId, offset: start + 1 }, handled: true };
  }
  if (text === "" && isListType(b.type)) {
    if (b.parentId !== undefined) return { ops: [...ops, ...outdentOps(doc, blockId)], focus: { blockId, offset: 0 }, handled: true };
    ops.push({ op: "setType", id: blockId, type: "paragraph" }, { op: "setProps", id: blockId, props: keepColor(b.props, {}) });
    return { ops, focus: { blockId, offset: 0 }, handled: true };
  }
  const type = continuation(b.type);
  const props = defaultProps(type);
  const parent = b.parentId;
  const index = indexOf(doc, blockId);
  if (start === 0 && text !== "") {
    ops.push(parent === undefined ? { op: "insertBlock", id: newId, index, type, props, text: "" } : { op: "insertBlock", id: newId, parentId: parent, index, type, props, text: "" });
    return { ops, focus: { blockId, offset: 0 }, handled: true };
  }
  const intoChildren = b.children.length > 0 || b.type === "toggle";
  if (intoChildren) ops.push({ op: "insertBlock", id: newId, parentId: blockId, index: 0, type, props, text: "" });
  else ops.push(parent === undefined ? { op: "insertBlock", id: newId, index: index + 1, type, props, text: "" } : { op: "insertBlock", id: newId, parentId: parent, index: index + 1, type, props, text: "" });
  if (start < text.length) ops.push({ op: "moveText", from: blockId, start, end: text.length, to: newId, at: 0 });
  return { ops, focus: { blockId: newId, offset: 0 }, handled: true };
}

/** The block a top-level block merges into on Backspace: the last visible block above it. */
function mergeTarget(doc: EditorSnapshot, id: string, collapsed: string[]): string | undefined {
  return previousVisible(doc, id, collapsed);
}

/**
 * Backspace with the caret at offset 0 (no selection).
 * - a non-paragraph text block becomes a paragraph (headings, lists, quotes, …);
 * - a nested paragraph outdents;
 * - a top-level paragraph merges into the block above (its text appended, its children following it); a divider
 *   above is deleted, any other block without text above is selected; nothing happens at the very top.
 */
export function backspaceAtStart(doc: EditorSnapshot, blockId: string, collapsed: string[]): EditResult {
  const b = block(doc, blockId);
  if (b === undefined || !hasText(b.type)) return NOT_HANDLED;
  if (b.type !== "paragraph") return { ...turnInto(doc, [blockId], "paragraph"), focus: { blockId, offset: 0 } };
  if (b.parentId !== undefined) return { ops: outdentOps(doc, blockId), focus: { blockId, offset: 0 }, handled: true };
  const targetId = mergeTarget(doc, blockId, collapsed);
  if (targetId === undefined) return HANDLED;
  const target = block(doc, targetId)!;
  if (target.type === "divider") return { ops: [{ op: "deleteBlock", id: targetId }], focus: { blockId, offset: 0 }, handled: true };
  if (!hasText(target.type)) return { ops: [], select: [targetId], handled: true };
  return mergeInto(doc, blockId, targetId);
}

/** Appends `id`'s text to `targetId` and deletes `id`; its children move to right after `id`'s old place. */
function mergeInto(doc: EditorSnapshot, id: string, targetId: string): EditResult {
  const b = block(doc, id)!;
  const target = block(doc, targetId)!;
  const ops: EditOp[] = [];
  if (b.text.length > 0) ops.push({ op: "moveText", from: id, start: 0, end: b.text.length, to: targetId, at: target.text.length });
  // Children are re-homed among b's siblings (where they appear on screen), then b goes.
  let d = applyOps(doc, ops);
  const at = indexOf(d, id);
  b.children.forEach((c, i) => {
    const op: EditOp = b.parentId === undefined ? { op: "moveBlock", id: c, index: at + 1 + i } : { op: "moveBlock", id: c, parentId: b.parentId, index: at + 1 + i };
    ops.push(op);
    d = applyOps(d, [op]);
  });
  ops.push({ op: "deleteBlock", id });
  return { ops, focus: { blockId: targetId, offset: target.text.length }, handled: true };
}

/**
 * Delete (forward) with the caret at the end of a text block: the next visible text block merges into this one;
 * a divider below is deleted; any other block without text below is selected.
 */
export function deleteAtEnd(doc: EditorSnapshot, blockId: string, collapsed: string[]): EditResult {
  const b = block(doc, blockId);
  if (b === undefined || !hasText(b.type)) return NOT_HANDLED;
  const nextId = nextVisible(doc, blockId, collapsed);
  if (nextId === undefined) return HANDLED;
  const next = block(doc, nextId)!;
  if (next.type === "divider") return { ops: [{ op: "deleteBlock", id: nextId }], focus: { blockId, offset: b.text.length }, handled: true };
  if (!hasText(next.type)) return { ops: [], select: [nextId], handled: true };
  return mergeInto(doc, nextId, blockId);
}

/**
 * Deletes the selected blocks (with their subtrees). The caret goes to the end of the nearest text block above
 * (or below); an emptied page gets a fresh paragraph `newId`.
 */
export function deleteBlocks(doc: EditorSnapshot, ids: string[], newId: string): EditResult {
  const roots = selectionRoots(doc, ids);
  if (roots.length === 0) return HANDLED;
  const ops: EditOp[] = roots.map((id) => ({ op: "deleteBlock", id }) as EditOp);
  const order = visibleRows(doc, []).map((r) => r.id);
  const gone = new Set(roots.flatMap((r) => subtree(doc, r)));
  const first = order.indexOf(roots[0]!);
  let focus: Caret | undefined;
  for (let i = first - 1; i >= 0 && focus === undefined; i--) {
    const x = block(doc, order[i]!)!;
    if (!gone.has(x.id) && hasText(x.type)) focus = { blockId: x.id, offset: x.text.length };
  }
  for (let i = first + 1; i < order.length && focus === undefined; i++) {
    const x = block(doc, order[i]!)!;
    if (!gone.has(x.id) && hasText(x.type)) focus = { blockId: x.id, offset: 0 };
  }
  if (focus === undefined) {
    const remaining = doc.roots.filter((r) => !gone.has(r)).length;
    ops.push({ op: "insertBlock", id: newId, index: remaining, type: "paragraph", props: {}, text: "" });
    focus = { blockId: newId, offset: 0 };
  }
  return { ops, focus, handled: true };
}

/** An empty page gets one empty paragraph to type into. */
export function ensureBlock(doc: EditorSnapshot, newId: string): EditResult {
  if (doc.roots.length > 0) return { ops: [], handled: false };
  return { ops: [{ op: "insertBlock", id: newId, index: 0, type: "paragraph", props: {}, text: "" }], focus: { blockId: newId, offset: 0 }, handled: true };
}

/** Inserts a block of `type` after `afterId` (or at the end of the page) and puts the caret in it. */
export function insertAfter(doc: EditorSnapshot, afterId: string | undefined, type: BlockType, props: BlockProps, newId: string): EditResult {
  const after = afterId === undefined ? undefined : block(doc, afterId);
  const parent = after?.parentId;
  const index = after === undefined ? doc.roots.length : indexOf(doc, after.id) + 1;
  const op: EditOp = parent === undefined ? { op: "insertBlock", id: newId, index, type, props, text: "" } : { op: "insertBlock", id: newId, parentId: parent, index, type, props, text: "" };
  return hasText(type) ? { ops: [op], focus: { blockId: newId, offset: 0 }, handled: true } : { ops: [op], handled: true };
}

/** Moves the selected blocks one step up or down among their siblings (⌘⇧↑ / ⌘⇧↓). */
export function moveBlocks(doc: EditorSnapshot, ids: string[], direction: "up" | "down"): EditResult {
  const roots = selectionRoots(doc, ids);
  const first = roots[0];
  if (first === undefined) return HANDLED;
  const parent = block(doc, first)!.parentId;
  if (roots.some((r) => block(doc, r)!.parentId !== parent)) return HANDLED;
  const sibs = childrenOf(doc, parent);
  const idx = roots.map((r) => sibs.indexOf(r)).toSorted((a, b) => a - b);
  const lo = idx[0]!;
  const hi = idx[idx.length - 1]!;
  if (direction === "up" ? lo === 0 : hi === sibs.length - 1) return HANDLED;
  const other = direction === "up" ? sibs[lo - 1]! : sibs[hi + 1]!;
  // Moving the neighbour across the selection is one op and keeps the selection intact.
  const index = direction === "up" ? hi : lo;
  const op: EditOp = parent === undefined ? { op: "moveBlock", id: other, index } : { op: "moveBlock", id: other, parentId: parent, index };
  return { ops: [op], handled: true };
}

// ---------- Inline formatting ----------

/** ⌘B / ⌘I / ⌘⇧S / ⌘E / ⌘U on [start, end): removes the mark when the whole range has it, adds it otherwise. */
export function toggleMark(doc: EditorSnapshot, blockId: string, start: number, end: number, mark: FormatMark): EditResult {
  const b = block(doc, blockId);
  if (b === undefined || !hasText(b.type) || end <= start) return HANDLED;
  return { ops: [{ op: "format", id: blockId, start, end, mark, on: !hasMark(b, start, end, mark) }], handled: true };
}

/** Sets or clears (empty url) a link on [start, end). */
export function setLink(doc: EditorSnapshot, blockId: string, start: number, end: number, url: string): EditResult {
  const b = block(doc, blockId);
  if (b === undefined || end <= start) return HANDLED;
  const u = url.trim();
  return { ops: [u === "" ? { op: "link", id: blockId, start, end } : { op: "link", id: blockId, start, end, url: normalizeLink(u) }], focus: { blockId, offset: end }, handled: true };
}

/** "example.com" → "https://example.com"; keeps explicit schemes and mailto:. */
export function normalizeLink(url: string): string {
  return /^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `https://${url}`;
}

// ---------- Text edits ----------

/** Replaces [start, end) of a block's text with `text` (typing, IME commits, cut). */
export function replaceText(doc: EditorSnapshot, blockId: string, start: number, end: number, text: string): EditResult {
  const b = block(doc, blockId);
  if (b === undefined || !hasText(b.type)) return NOT_HANDLED;
  const ops: EditOp[] = [];
  if (end > start) ops.push({ op: "deleteText", id: blockId, index: start, length: end - start });
  if (text !== "") ops.push({ op: "insertText", id: blockId, index: start, text });
  return { ops, focus: { blockId, offset: start + text.length }, handled: true };
}

/** Line prefixes that turn a paragraph into another block (typed at the start, or at the start of a pasted line). */
type Shortcut = { type: BlockType; props: BlockProps };

function shortcutFor(prefix: string): Shortcut | undefined {
  switch (prefix) {
    case "# ":
      return { type: "heading1", props: {} };
    case "## ":
      return { type: "heading2", props: {} };
    case "### ":
      return { type: "heading3", props: {} };
    case "- ":
    case "* ":
    case "+ ":
      return { type: "bulleted", props: {} };
    case "[] ":
    case "[ ] ":
      return { type: "todo", props: { checked: false } };
    case "[x] ":
    case "[X] ":
      return { type: "todo", props: { checked: true } };
    case "> ":
    case "\" ":
      return { type: "quote", props: {} };
    case "```":
      return { type: "code", props: { language: "plain" } };
  }
  if (/^\d{1,3}[.)] $/.test(prefix)) return { type: "numbered", props: {} };
  return undefined;
}

/**
 * Length of a markdown shortcut prefix at the start of `text` ("# ", "- ", "1. ", "[] ", "> ", "```", or "---" as the
 * whole text), 0 when there is none. Lets an edit that typed or pasted the prefix together with more text (IME,
 * autocomplete, dictation) still trigger the shortcut.
 */
export function shortcutPrefixLength(text: string): number {
  if (text === "---") return 3;
  const m = /^(#{1,3} |[-*+] |\[[ xX]?\] |> |" |\d{1,3}[.)] |```)/.exec(text);
  return m === null || shortcutFor(m[1]!) === undefined ? 0 : m[1]!.length;
}

/**
 * Markdown shortcuts, checked after each keystroke in a paragraph: "# ", "## ", "### ", "- ", "* ", "1. ",
 * "[] ", "[x] ", "> " and "```" at the start turn it into a heading, list, to-do, quote or code block; "---" as
 * the whole text makes a divider followed by a new paragraph `newId`.
 */
export function markdownShortcut(doc: EditorSnapshot, blockId: string, caret: number, newId: string): EditResult {
  const b = block(doc, blockId);
  if (b === undefined || b.type !== "paragraph" || caret <= 0 || caret > b.text.length) return NOT_HANDLED;
  const prefix = b.text.slice(0, caret);
  if (prefix === "---" && b.text === "---") {
    const parent = b.parentId;
    const index = indexOf(doc, blockId) + 1;
    return {
      ops: [
        { op: "deleteText", id: blockId, index: 0, length: 3 },
        { op: "setType", id: blockId, type: "divider" },
        parent === undefined ? { op: "insertBlock", id: newId, index, type: "paragraph", props: {}, text: "" } : { op: "insertBlock", id: newId, parentId: parent, index, type: "paragraph", props: {}, text: "" },
      ],
      focus: { blockId: newId, offset: 0 },
      handled: true,
    };
  }
  const s = shortcutFor(prefix);
  if (s === undefined || prefix.includes(ATOM)) return NOT_HANDLED;
  return {
    ops: [
      { op: "deleteText", id: blockId, index: 0, length: caret },
      { op: "setType", id: blockId, type: s.type },
      { op: "setProps", id: blockId, props: keepColor(b.props, s.props) },
    ],
    focus: { blockId, offset: 0 },
    handled: true,
  };
}

/**
 * Pasting plain text at [start, end): one line is inserted in place; several lines become blocks (the first joins
 * the current block, the rest are new blocks of the continuing type, each honouring a markdown prefix) and the
 * text after the caret ends up after the last pasted line.
 */
export function pasteText(doc: EditorSnapshot, blockId: string, start: number, end: number, text: string, newIds: string[]): EditResult {
  const b = block(doc, blockId);
  if (b === undefined || !hasText(b.type)) return NOT_HANDLED;
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  if (b.type === "code" || lines.length === 1) return replaceText(doc, blockId, start, end, b.type === "code" ? lines.join("\n") : lines[0]!);
  const ops: EditOp[] = [];
  if (end > start) ops.push({ op: "deleteText", id: blockId, index: start, length: end - start });
  if (lines[0] !== "") ops.push({ op: "insertText", id: blockId, index: start, text: lines[0]! });
  const tailStart = start + lines[0]!.length;
  const tailEnd = b.text.length - (end - start) + lines[0]!.length;
  const parent = b.parentId;
  let index = indexOf(doc, blockId) + 1;
  let last: Caret = { blockId, offset: tailStart };
  const rest = lines.slice(1);
  rest.forEach((line, i) => {
    const id = newIds[i];
    if (id === undefined) return;
    const m = /^(#{1,3} |[-*+] |\[[ xX]?\] |> |\d{1,3}[.)] )/.exec(line);
    const s = m === null ? undefined : shortcutFor(m[1]!);
    const body = s === undefined ? line : line.slice(m![1]!.length);
    const type = s?.type ?? continuation(b.type);
    const props = s?.props ?? defaultProps(type);
    ops.push(parent === undefined ? { op: "insertBlock", id, index, type, props, text: body } : { op: "insertBlock", id, parentId: parent, index, type, props, text: body });
    index += 1;
    last = { blockId: id, offset: body.length };
  });
  if (tailEnd > tailStart && last.blockId !== blockId) ops.push({ op: "moveText", from: blockId, start: tailStart, end: tailEnd, to: last.blockId, at: last.offset });
  return { ops, focus: last, handled: true };
}

// ---------- Slash menu ----------

export type SlashItemId =
  | "paragraph" | "heading1" | "heading2" | "heading3" | "bulleted" | "numbered" | "todo" | "toggle" | "quote" | "callout"
  | "code" | "divider" | "image" | "file" | "child_page" | "link_to_page" | "child_database";

/** The block catalogue in menu order. `keywords` are English aliases matched in every locale. */
export const SLASH_ITEMS: { id: SlashItemId; type: BlockType; keywords: string[] }[] = [
  { id: "paragraph", type: "paragraph", keywords: ["text", "plain", "paragraph"] },
  { id: "heading1", type: "heading1", keywords: ["h1", "heading", "title"] },
  { id: "heading2", type: "heading2", keywords: ["h2", "heading", "subtitle"] },
  { id: "heading3", type: "heading3", keywords: ["h3", "heading"] },
  { id: "bulleted", type: "bulleted", keywords: ["bullet", "list", "ul"] },
  { id: "numbered", type: "numbered", keywords: ["number", "ordered", "list", "ol"] },
  { id: "todo", type: "todo", keywords: ["todo", "task", "checkbox", "check"] },
  { id: "toggle", type: "toggle", keywords: ["toggle", "collapse", "details"] },
  { id: "quote", type: "quote", keywords: ["quote", "blockquote", "citation"] },
  { id: "callout", type: "callout", keywords: ["callout", "note", "tip", "info"] },
  { id: "code", type: "code", keywords: ["code", "snippet", "pre"] },
  { id: "divider", type: "divider", keywords: ["divider", "separator", "line", "hr"] },
  { id: "image", type: "image", keywords: ["image", "picture", "photo", "img"] },
  { id: "file", type: "file", keywords: ["file", "attachment", "upload"] },
  { id: "child_page", type: "child_page", keywords: ["page", "subpage", "new"] },
  { id: "link_to_page", type: "link_to_page", keywords: ["link", "page", "mention"] },
  { id: "child_database", type: "child_database", keywords: ["database", "board", "kanban", "projects", "table"] },
];

/** An open "/query" before the caret: "/" at the start or after a space, then up to 24 characters on one line. */
export function slashQuery(text: string, caret: number): { start: number; query: string } | undefined {
  return triggerQuery(text, caret, "/");
}

/** An open "@query" before the caret (mention picker). */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | undefined {
  return triggerQuery(text, caret, "@");
}

function triggerQuery(text: string, caret: number, trigger: string): { start: number; query: string } | undefined {
  const head = text.slice(0, caret);
  const at = head.lastIndexOf(trigger);
  if (at < 0) return undefined;
  if (at > 0 && !/\s/.test(head.charAt(at - 1))) return undefined;
  const query = head.slice(at + 1);
  if (query.length > 24 || /[\n￼]/.test(query) || query.startsWith(" ") || query.includes("  ")) return undefined;
  return { start: at, query };
}

function fold(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
}

/**
 * Slash items matching `query`, best first: the label starts with it, then a word of the label, then an English
 * keyword, then the label contains it (ties keep catalogue order). `labels` are the translated item labels.
 */
export function filterSlash(query: string, labels: Record<string, string>): SlashItemId[] {
  const q = fold(query);
  if (q === "") return SLASH_ITEMS.map((i) => i.id);
  const scored: { id: SlashItemId; score: number; order: number }[] = [];
  SLASH_ITEMS.forEach((item, order) => {
    const label = fold(labels[item.id] ?? item.id);
    let score = 0;
    if (label.startsWith(q)) score = 4;
    else if (label.split(/[^\p{L}\p{N}]+/u).some((w) => w !== "" && w.startsWith(q))) score = 3;
    else if (item.keywords.some((k) => k.startsWith(q))) score = 2;
    else if (label.includes(q)) score = 1;
    if (score > 0) scored.push({ id: item.id, score, order });
  });
  return scored.toSorted((a, b) => b.score - a.score || a.order - b.order).map((s) => s.id);
}

/**
 * Runs a slash item: removes the "/query" text [start, end), then turns an empty block into `type` (or inserts a
 * new block of `type` below a non-empty one). A divider is followed by a fresh paragraph. `ids` are fresh block ids.
 */
export function applySlash(doc: EditorSnapshot, blockId: string, start: number, end: number, type: BlockType, props: BlockProps, ids: string[]): EditResult {
  const b = block(doc, blockId);
  if (b === undefined) return NOT_HANDLED;
  const ops: EditOp[] = end > start ? [{ op: "deleteText", id: blockId, index: start, length: end - start }] : [];
  const remaining = b.text.length - (end - start);
  const parent = b.parentId;
  const at = indexOf(doc, blockId);
  const ins = (id: string, index: number, t: BlockType, p: BlockProps): EditOp =>
    parent === undefined ? { op: "insertBlock", id, index, type: t, props: p, text: "" } : { op: "insertBlock", id, parentId: parent, index, type: t, props: p, text: "" };
  const [a, c] = ids;
  if (remaining === 0 && hasText(b.type)) {
    if (hasText(type)) {
      if (b.type !== type) ops.push({ op: "setType", id: blockId, type });
      ops.push({ op: "setProps", id: blockId, props: keepColor(b.props, props) });
      return { ops, focus: { blockId, offset: 0 }, handled: true };
    }
    // A void block replaces the empty one; the caret moves to a paragraph below.
    ops.push({ op: "setType", id: blockId, type }, { op: "setProps", id: blockId, props });
    if (a !== undefined) {
      ops.push(ins(a, at + 1, "paragraph", {}));
      return { ops, focus: { blockId: a, offset: 0 }, handled: true };
    }
    return { ops, handled: true };
  }
  if (a === undefined) return { ops, handled: true };
  ops.push(ins(a, at + 1, type, props));
  if (hasText(type)) return { ops, focus: { blockId: a, offset: 0 }, handled: true };
  if (c !== undefined) {
    ops.push(ins(c, at + 2, "paragraph", {}));
    return { ops, focus: { blockId: c, offset: 0 }, handled: true };
  }
  return { ops, handled: true };
}

/** Replaces the "@query" text [start, end) with a mention atom followed by a space. */
export function applyMention(doc: EditorSnapshot, blockId: string, start: number, end: number, mention: MentionMark): EditResult {
  const b = block(doc, blockId);
  if (b === undefined || !hasText(b.type)) return NOT_HANDLED;
  const ops: EditOp[] = [];
  if (end > start) ops.push({ op: "deleteText", id: blockId, index: start, length: end - start });
  ops.push({ op: "insertMention", id: blockId, index: start, mention }, { op: "insertText", id: blockId, index: start + 1, text: " " });
  return { ops, focus: { blockId, offset: start + 2 }, handled: true };
}

// ---------- To-dos, toggles ----------

/** Checks / unchecks a to-do. */
export function setChecked(doc: EditorSnapshot, blockId: string, checked: boolean): EditResult {
  const b = block(doc, blockId);
  if (b === undefined || b.type !== "todo") return HANDLED;
  return { ops: [{ op: "setProps", id: blockId, props: { ...b.props, checked } }], handled: true };
}

/** Collapsed toggles that must open so `blockId` is visible (its collapsed toggle ancestors). */
export function revealAncestors(doc: EditorSnapshot, blockId: string, collapsed: string[]): string[] {
  const out: string[] = [];
  let p = block(doc, blockId)?.parentId;
  while (p !== undefined) {
    if (collapsed.includes(p)) out.push(p);
    p = block(doc, p)?.parentId;
  }
  return out;
}

/** Page ids referenced by child_page / link_to_page / child_database blocks (in reading order). */
export function referencedPages(doc: EditorSnapshot): string[] {
  const out: string[] = [];
  for (const id of documentOrder(doc)) {
    const b = doc.blocks[id]!;
    if ((b.type === "child_page" || b.type === "link_to_page" || b.type === "child_database") && b.props.pageId !== undefined && !out.includes(b.props.pageId)) out.push(b.props.pageId);
  }
  return out;
}

/** Plain text of the whole body (search extracts, previews): one line per text block. */
export function plainText(doc: EditorSnapshot): string {
  return documentOrder(doc)
    .map((id) => doc.blocks[id]!)
    .filter((b) => hasText(b.type))
    .map((b) => b.text.replaceAll(ATOM, ""))
    .join("\n");
}
